import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./ProductListScreen.css";
import { formatCurrency } from "../../../format/formatDate";
import ModalEnrollment from "../enrollment/ModalEnrollment";

const ProductListScreen = () => {
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(categorySlug || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 32;

  // Fetch categories and initial products
  useEffect(() => {
    const initializeData = async () => {
      await fetchCategories();
      await fetchProducts();
    };
    initializeData();
  }, []);

  // Update selected category when URL changes
  useEffect(() => {
    setSelectedCategory(categorySlug || "");
    setCurrentPage(1);
    setProducts([]);
  }, [categorySlug]);

  // Fetch products when category or page changes
  useEffect(() => {
    if (currentPage > 1 || selectedCategory !== undefined) {
      fetchProducts();
    }
  }, [selectedCategory, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get("/category/public/shows");
      const data = response.data?.data || {};
      setCategories(data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let url;

      if (searchQuery.trim()) {
        url = `/product/public/shows?keyword=${encodeURIComponent(searchQuery.trim())}&page=${currentPage}&limit=${itemsPerPage}`;
      } else {
        url = selectedCategory
          ? `/product/public/shows?categorySlugs=${selectedCategory}&page=${currentPage}&limit=${itemsPerPage}`
          : `/product/public/shows?page=${currentPage}&limit=${itemsPerPage}`;
      }

      const response = await axios.get(url);
      const data = response.data?.data || {};

      if (currentPage === 1) {
        setProducts(data.products || []);
      } else {
        setProducts(prev => [...prev, ...(data.products || [])]);
      }

      setTotalPages(Math.ceil((data.totalItems || 0) / itemsPerPage));
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryPress = slug => {
    if (slug === selectedCategory) return;
    navigate(slug ? `/san-pham/${slug}` : "/san-pham");
  };

  const handleProductPress = product => {
    navigate(`/san-pham/${product.category.slug}/${product.slug}`);
  };

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const getFirstImage = imagesUrl => {
    try {
      if (!imagesUrl) return "";
      return imagesUrl[0] || "";
    } catch (error) {
      console.error("Error parsing imagesUrl:", error);
      return "";
    }
  };

  const handleContactClick = (e, product) => {
    e.stopPropagation();
    setSelectedProduct(product);
    setShowEnrollment(true);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setCurrentPage(1);
      setProducts([]);
      fetchProducts();
    }
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
    setProducts([]);
    fetchProducts();
  };

  return (
    <div className="product-list-container">
      <div className="search-container">
        <div className="search-input-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Tìm kiếm sản phẩm..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          {searchQuery && (
            <button className="clear-button" onClick={handleClearSearch}>
              ×
            </button>
          )}
          <button className="search-button" onClick={handleSearch}>
            <i className="fas fa-search"></i>
          </button>
        </div>
      </div>

      <div className="category-list">
        <button
          className={`category-item ${selectedCategory === "" ? "selected" : ""}`}
          onClick={() => handleCategoryPress("")}
        >
          Tất cả
        </button>
        {categories?.length > 0 &&
          categories?.map(category => (
            <button
              key={category.id}
              className={`category-item ${selectedCategory === category.slug ? "selected" : ""}`}
              onClick={() => handleCategoryPress(category.slug)}
            >
              {category.name}
            </button>
          ))}
      </div>

      {loading && products.length === 0 ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          <div className="product-grid">
            {products.map(product => (
              <div
                key={product.id}
                className="product-card"
                onClick={() => handleProductPress(product)}
              >
                <img
                  src={getFirstImage(product.imagesUrl)}
                  alt={product.name}
                  className="product-image"
                  onError={e => {
                    e.target.onerror = null;
                    e.target.src = "/placeholder-image.jpg";
                  }}
                />
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  {product?.salePrice && Number(product?.salePrice) > 0 ? (
                    <p className="product-price">
                      {product.originalPrice ? (
                        <span className="origin-price me-2">
                          {formatCurrency(product.originalPrice)} đ
                        </span>
                      ) : null}
                      {formatCurrency(product.salePrice)} đ
                    </p>
                  ) : null}

                  <button
                    className="contact-button"
                    onClick={e => handleContactClick(e, product)}
                  >
                    Liên hệ
                  </button>
                </div>
              </div>
            ))}
          </div>

          {products.length === 0 && (
            <div className="no-products-container">
              <p className="no-products-text">Không có sản phẩm nào</p>
            </div>
          )}

          {currentPage < totalPages && (
            <div className="load-more-container">
              <button
                className="load-more-button"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? "Đang tải..." : "Xem thêm"}
              </button>
            </div>
          )}
        </>
      )}

      <ModalEnrollment
        show={showEnrollment}
        onHide={() => setShowEnrollment(false)}
        subject={`Cần mua sản phẩm: ${selectedProduct?.name}`}
      />
    </div>
  );
};

export default ProductListScreen;
